import {noop} from '@benchling/js-utils';
import React from 'react';

import EntryActions from 'actions/EntryActions';
import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BasicInformationPanelState} from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import CreateEntryFromTemplateModal from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import notifications from 'notifications';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from 'toolbar/ReactPanelBaseView';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import APIUtil from 'util/APIUtil';

import {TemplateCollectionGraphQL} from '../../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';

interface Props {
  templateId: string;
  initialName: string;
  initialCollection: TemplateCollectionGraphQL;
  archiveRecord: ArchiveRecord | null;
  permissions: Permissions;
  saveModel: (name: string, id: string) => void;
  deleteModel: () => void;
}

interface State {
  name: string;
  collection: TemplateCollectionGraphQL;
}

export class BaseTemplateInformationSubpanel extends React.Component<Props, State> {
  override state: State = {
    name: this.props.initialName,
    collection: this.props.initialCollection,
  };

  _handleChangeCollection = (collection: TemplateCollectionGraphQL): void => {
    this.setState({collection});
  };

  saveModel = (): void => {
    this.props.saveModel(this.state.name, this.state.collection.id);
  };

  _exportAuditLogs = (): void => {
    AuditActions.exportAuditLogs([this.props.templateId]);
  };

  handleDelete = async (): Promise<void> => {
    if (await runConfirmDeleteModal({typeName: 'template'})) {
      this.props.deleteModel();
    }
  };

  _handleCreateEntryFromTemplate = ({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
  }: {
    entryName: string;
    folderId: string;
    templateId: string;
    tagSchemaId?: string;
    schemaTags?: TagUpdateObject;
    initialDay: string;
  }): void => {
    EntryActions.createFromTemplate({
      entryName,
      folderId,
      templateId,
      tagSchemaId,
      schemaTags,
      initialDay,
      /* eslint-disable-next-line @typescript-eslint/unbound-method */
      onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
    });
  };

  showCreateEntryFromTemplateModal = (): void => {
    // TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal
    if (!BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
      LegacyCreateEntryFromTemplateModalClass.open({
        initialTemplateId: this.props.templateId,
        initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
        onRequestSubmit: this._handleCreateEntryFromTemplate,
      });
    } else {
      legacyRunModal(CreateEntryFromTemplateModal, {
        initialTemplateId: this.props.templateId,
        initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
        onRequestClose: () => {},
        onRequestSubmit: this._handleCreateEntryFromTemplate,
        isTemplateSelectorDisabled: false,
      });
    }
  };

  override render(): JSX.Element {
    const templateType = APIUtil.apiIdToMaybeType(this.props.templateId);
    const {writable, archivable, unarchivable} = this.props.permissions;

    return (
      <form>
        <div className="form-group u-small-spacing-top">
          <Row>
            <Col md={4}>
              <h5>Name</h5>
            </Col>
            <Col md={8}>
              <input
                type="text"
                disabled={!writable || this.props.archiveRecord != null}
                value={this.state.name}
                onChange={(e) => {
                  this.setState({name: e.target.value});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <h5>Collection</h5>
            </Col>
            <Col md={8}>
              <TemplateCollectionSelector
                value={this.state.collection}
                onChange={this.props.permissions.writable ? this._handleChangeCollection : noop}
              />
            </Col>
          </Row>
        </div>
        {writable && (
          <Row key="update-btn">
            <Col md={12}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                fullWidth={true}
                size={ButtonSize.SMALL}
                disabled={this.props.archiveRecord != null}
                onClick={this.saveModel}
                text="Update Information"
              />
            </Col>
          </Row>
        )}
        <div className="panel-divider" key="update-create-divider" />
        {templateType === ModelTypeConstants.TEMPLATE && (
          <Row>
            <Col md={12}>
              <Button
                id="create-entry-button"
                buttonStyle={ButtonStyle.SECONDARY}
                fullWidth={true}
                size={ButtonSize.SMALL}
                disabled={this.props.archiveRecord != null}
                onClick={this.showCreateEntryFromTemplateModal}
                text="Create entry"
              />
            </Col>
          </Row>
        )}
        {/* needs archiving permissions check here */}
        {BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING ? (
          <Row key="archive-btn">
            <Col md={12}>
              <ArchiveOrUnarchiveButton
                fullWidth={true}
                isArchivable={Boolean(archivable)}
                isUnarchivable={Boolean(unarchivable)}
                item={{
                  id: this.props.templateId,
                  name: this.props.initialName,
                  archiveRecord: this.props.archiveRecord,
                }}
                onArchive={(
                  ids: Array<string>,
                  purpose: string,
                  options: ArchiveActions.BulkArchiveFilesOptions
                ) => {
                  ArchiveActions.bulkArchiveFilesWithDataMods(ids, purpose, options);
                }}
                onUnarchive={(ids: Array<string>) => {
                  ArchiveActions.bulkUnarchiveFilesWithDataMods(ids, {});
                }}
                showText={true}
              />
            </Col>
          </Row>
        ) : (
          writable &&
          BenchlingConfig.ENABLE_DELETE && (
            <Row key="delete-btn">
              <Col md={12}>
                <Button
                  buttonStyle={ButtonStyle.NEGATIVE}
                  fullWidth={true}
                  size={ButtonSize.SMALL}
                  onClick={this.handleDelete}
                  text={
                    templateType === ModelTypeConstants.SUBTEMPLATE
                      ? 'Delete sub-template'
                      : 'Delete template'
                  }
                />
              </Col>
            </Row>
          )
        )}
        <div className="panel-divider" key="archive-audit-divider" />
        <Row>
          <Col md={12}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              fullWidth={true}
              onClick={this._exportAuditLogs}
              size={ButtonSize.SMALL}
              text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
              dataTestAttrs={{
                component: 'TemplateInformationPanelView',
                element: 'exportAuditLogButton',
              }}
            />
          </Col>
        </Row>
      </form>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class TemplateInformationPanelView extends ReactPanelBaseView<BasicInformationPanelState, any> {
  saveModel = (name: string, templateCollectionId: string): void => {
    TemplateActions.saveFileData(this.model.id, {
      data: {name, templateCollectionId},
      success: this.onSaveSuccess,
    });
  };

  onSaveSuccess = (): void => {
    notifications.success('Template successfully updated.');
  };

  deleteModel = (): void => {
    TemplateActions.delete(this.model.id, this.model.templateCollection().id);
  };

  getSubpanels = (): Array<Subpanel> => [
    {
      ...getTabAndPanelIds('information'),
      title: 'Information',
      component: (
        <BaseTemplateInformationSubpanel
          templateId={this.model.id}
          initialName={this.model.name()}
          initialCollection={this.model.templateCollection()}
          archiveRecord={this.model.archiveRecord()}
          permissions={this.model.permissions()}
          saveModel={this.saveModel}
          deleteModel={this.deleteModel}
        />
      ),
    },
  ];
}

export default TemplateInformationPanelView;
